import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// Code Refactoring nodes - showing evolution of code structure and quality
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisCodeRefactoringPositions');
  if (savedPositions) {
    return JSON.parse(savedPositions);
  }
  
  return [
    // User Intent
    {
      id: 'user-intent',
      data: { label: '💬 "Refactor this legacy codebase for better maintainability"' },
      position: { x: 400, y: 50 },
      style: themeStyles.problemNode,
    },
    
    // Code Analysis
    {
      id: 'code-analysis',
      data: { label: '📊 Code Analysis\n• 15,000 LOC\n• Cyclomatic complexity: 45\n• Duplication: 35%\n• Tech debt: High' },
      position: { x: 800, y: 50 },
      style: { ...themeStyles.analysisNode, width: 220 },
    },
    
    // Initial Legacy Code
    {
      id: 'initial-code',
      type: 'collapsibleCode',
      data: { 
        filename: 'legacy-payment.js',
        code: '// Legacy payment processing\n' +
              'function processPayment(amount, card, customer) {\n' +
              '  // TODO: Refactor this mess\n' +
              '  if (card && card.number && card.cvv && card.expiry) {\n' +
              '    if (amount > 0) {\n' +
              '      if (customer.status === "active") {\n' +
              '        // Validate card\n' +
              '        if (card.number.length === 16) {\n' +
              '          // Process payment\n' +
              '          var result = chargeCard(card, amount);\n' +
              '          if (result.success) {\n' +
              '            updateCustomerBalance(customer.id, amount);\n' +
              '            sendEmail(customer.email, "Payment successful");\n' +
              '            logTransaction(amount, card.number);\n' +
              '            return { success: true, id: result.transactionId };\n' +
              '          }\n' +
              '        }\n' +
              '      }\n' +
              '    }\n' +
              '  }\n' +
              '  return { success: false, error: "Payment failed" };\n' +
              '}',
        issues: 'Deep nesting, no error handling, mixed responsibilities, poor naming',
        score: 1.5
      },
      position: { x: 400, y: 200 },
    },
    
    // Scoring System
    {
      id: 'scoring-label',
      data: { label: '🎯 Code Quality Metrics' },
      position: { x: 1100, y: 200 },
      style: { ...themeStyles.metricNode, width: 240 },
    },
    {
      id: 'complexity-score',
      data: { label: '🔄 Complexity\nCyclomatic: 12\nNesting: 6 levels' },
      position: { x: 950, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'maintainability-score',
      data: { label: '🔧 Maintainability\nScore: 2/10\nTech debt: 4h' },
      position: { x: 1100, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'readability-score',
      data: { label: '📖 Readability\nScore: 3/10\nFlesch index: 45' },
      position: { x: 1250, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'test-feedback',
      data: { label: '⚡ Test Results\n✅ 85% tests still pass\n⏱️ Coverage maintained' },
      position: { x: 1025, y: 380 },
      style: themeStyles.suggestionNode,
    },
    {
      id: 'linter-feedback',
      data: { label: '🔍 Static Analysis\n12 code smells\n3 security warnings' },
      position: { x: 1175, y: 380 },
      style: themeStyles.suggestionNode,
    },
    
    // Genetic Algorithm Engine
    {
      id: 'genetic-algo',
      data: { label: '🧬 Refactoring Evolution Engine\n\n🔄 Mutation: Extract methods, rename variables\n🔗 Crossover: Combine best patterns\n📈 Selection: Optimize for readability + performance\n🎲 Population: 15 refactored variants' },
      position: { x: 100, y: 500 },
      style: { ...themeStyles.uniqueFeatureNode, width: 300, height: 140 },
    },
    
    // AI Agents with Prompts
    {
      id: 'ai-agents',
      data: { label: '🤖 Specialized Refactoring Agents' },
      position: { x: 500, y: 500 },
      style: { ...themeStyles.analysisNode, width: 260 },
    },
    {
      id: 'structure-agent',
      data: { label: '🏗️ Structure Agent\n\n📝 Prompt:\n"Extract functions to reduce complexity.\nEliminate deep nesting using\nearly returns and guard clauses.\nApply Single Responsibility\nPrinciple to each function."' },
      position: { x: 200, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'naming-agent',
      data: { label: '📝 Naming Agent\n\n📝 Prompt:\n"Improve variable and function\nnames for clarity. Use intention-\nrevealing names. Remove\nabbreviations and add\nmeaningful context."' },
      position: { x: 500, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'pattern-agent',
      data: { label: '🎨 Pattern Agent\n\n📝 Prompt:\n"Apply appropriate design\npatterns. Replace conditionals\nwith polymorphism where\nsuitable. Introduce strategy\npattern for complex logic."' },
      position: { x: 800, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    
    // Generation 1 Label
    {
      id: 'gen1-label',
      data: { label: '🌱 Generation 1: Initial Refactoring (Population: 15)' },
      position: { x: 500, y: 800 },
      style: { ...themeStyles.metricNode, width: 350 },
    },
    
    // Generation 1 Variants
    {
      id: 'gen1-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'payment-v1a.js (Structure Focus)',
        code: '// Reduced complexity variant\n' +
              '+ function processPayment(amount, card, customer) {\n' +
              '+   if (!isValidPaymentInput(amount, card, customer)) {\n' +
              '+     return createFailureResponse("Invalid input");\n' +
              '+   }\n' +
              '+   \n' +
              '+   const chargeResult = attemptCardCharge(card, amount);\n' +
              '+   if (!chargeResult.success) {\n' +
              '+     return createFailureResponse("Charge failed");\n' +
              '+   }\n' +
              '+   \n' +
              '+   return processSuccessfulPayment(customer, amount, chargeResult);\n' +
              '+ }\n' +
              '+\n' +
              '+ function isValidPaymentInput(amount, card, customer) {\n' +
              '+   return card?.number?.length === 16 &&\n' +
              '+          amount > 0 &&\n' +
              '+          customer?.status === "active";\n' +
              '+ }',
        score: 3.2
      },
      position: { x: 50, y: 900 },
    },
    {
      id: 'gen1-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'payment-v1b.js (Naming Focus)',
        code: '// Improved naming variant\n' +
              '+ class PaymentProcessor {\n' +
              '+   async processCustomerPayment(paymentAmount, creditCard, activeCustomer) {\n' +
              '+     const paymentValidationResult = this.validatePaymentRequirements(\n' +
              '+       paymentAmount, creditCard, activeCustomer\n' +
              '+     );\n' +
              '+     \n' +
              '+     if (!paymentValidationResult.isValid) {\n' +
              '+       return this.createPaymentFailureResponse(\n' +
              '+         paymentValidationResult.errorMessage\n' +
              '+       );\n' +
              '+     }\n' +
              '+     \n' +
              '+     const cardChargeResult = await this.chargeCustomerCard(\n' +
              '+       creditCard, paymentAmount\n' +
              '+     );\n' +
              '+     \n' +
              '+     return this.handleChargeResult(\n' +
              '+       cardChargeResult, activeCustomer, paymentAmount\n' +
              '+     );\n' +
              '+   }\n' +
              '+ }',
        score: 3.8
      },
      position: { x: 400, y: 900 },
    },
    {
      id: 'gen1-3',
      type: 'collapsibleCode',
      data: { 
        filename: 'payment-v1c.js (Pattern Focus)',
        code: '// Design patterns variant\n' +
              '+ class PaymentService {\n' +
              '+   constructor(validator, charger, notifier) {\n' +
              '+     this.validator = validator;\n' +
              '+     this.charger = charger;\n' +
              '+     this.notifier = notifier;\n' +
              '+   }\n' +
              '+   \n' +
              '+   async process(paymentRequest) {\n' +
              '+     try {\n' +
              '+       await this.validator.validate(paymentRequest);\n' +
              '+       const result = await this.charger.charge(paymentRequest);\n' +
              '+       await this.notifier.notifySuccess(result);\n' +
              '+       return PaymentResult.success(result.transactionId);\n' +
              '+     } catch (error) {\n' +
              '+       await this.notifier.notifyFailure(error);\n' +
              '+       return PaymentResult.failure(error.message);\n' +
              '+     }\n' +
              '+   }\n' +
              '+ }',
        score: 4.1
      },
      position: { x: 750, y: 900 },
    },
    {
      id: 'gen1-more',
      data: { label: '... 12 more variants\nwith different approaches' },
      position: { x: 950, y: 920 },
      style: { ...themeStyles.suggestionNode, width: 150 },
    },
    
    // Evolution Process
    {
      id: 'evolution-1',
      type: 'evolution',
      data: { 
        generation: '1→2',
        description: '🔄 Crossover: Best Patterns Combined',
        scores: {
          'Complexity': '6 → 3',
          'Readability': '3 → 7',
          'Maintainability': '2 → 6',
          'Test Coverage': '85% maintained'
        }
      },
      position: { x: 400, y: 1100 },
    },
    
    // Generation 2 Label
    {
      id: 'gen2-label',
      data: { label: '🧬 Generation 2: Hybrid Excellence (Best Practices Combined)' },
      position: { x: 500, y: 1250 },
      style: { ...themeStyles.metricNode, width: 450 },
    },
    
    // Generation 2 - Combined Features
    {
      id: 'gen2-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'payment-service.js (Elite Refactor)',
        code: '// Combined best practices from all variants\n' +
              '+ import { PaymentValidator } from "./validators/PaymentValidator";\n' +
              '+ import { CardProcessor } from "./processors/CardProcessor";\n' +
              '+ import { NotificationService } from "./services/NotificationService";\n' +
              '+ import { PaymentResult } from "./models/PaymentResult";\n' +
              '+\n' +
              '+ export class PaymentService {\n' +
              '+   constructor(dependencies = {}) {\n' +
              '+     this.validator = dependencies.validator || new PaymentValidator();\n' +
              '+     this.processor = dependencies.processor || new CardProcessor();\n' +
              '+     this.notifier = dependencies.notifier || new NotificationService();\n' +
              '+   }\n' +
              '+\n' +
              '+   async processPayment(paymentRequest) {\n' +
              '+     try {\n' +
              '+       this.validatePaymentRequest(paymentRequest);\n' +
              '+       \n' +
              '+       const chargeResult = await this.executeCardCharge(paymentRequest);\n' +
              '+       await this.handleSuccessfulPayment(paymentRequest, chargeResult);\n' +
              '+       \n' +
              '+       return PaymentResult.success({\n' +
              '+         transactionId: chargeResult.id,\n' +
              '+         amount: paymentRequest.amount,\n' +
              '+         timestamp: new Date().toISOString()\n' +
              '+       });\n' +
              '+     } catch (error) {\n' +
              '+       await this.handlePaymentFailure(error);\n' +
              '+       return PaymentResult.failure(error.message);\n' +
              '+     }\n' +
              '+   }\n' +
              '+ }',
        score: 4.7
      },
      position: { x: 200, y: 1350 },
    },
    {
      id: 'gen2-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'payment-validator.js (Supporting Class)',
        code: '// Mutation: Extracted validation logic\n' +
              '+ export class PaymentValidator {\n' +
              '+   validate(paymentRequest) {\n' +
              '+     this.validateAmount(paymentRequest.amount);\n' +
              '+     this.validateCard(paymentRequest.card);\n' +
              '+     this.validateCustomer(paymentRequest.customer);\n' +
              '+   }\n' +
              '+\n' +
              '+   validateAmount(amount) {\n' +
              '+     if (!amount || amount <= 0) {\n' +
              '+       throw new InvalidAmountError("Amount must be positive");\n' +
              '+     }\n' +
              '+   }\n' +
              '+\n' +
              '+   validateCard(card) {\n' +
              '+     if (!card?.number || card.number.length !== 16) {\n' +
              '+       throw new InvalidCardError("Invalid card number");\n' +
              '+     }\n' +
              '+     if (!this.isValidExpiryDate(card.expiry)) {\n' +
              '+       throw new InvalidCardError("Card expired");\n' +
              '+     }\n' +
              '+   }\n' +
              '+ }',
        score: 4.5
      },
      position: { x: 600, y: 1350 },
    },
    
    // Final Evolution
    {
      id: 'evolution-2',
      type: 'evolution',
      data: { 
        generation: '2→Final',
        description: '🏆 Production Ready',
        scores: {
          'Complexity': '3→2',
          'Readability': '7→9',
          'Maintainability': '6→9',
          'Test Coverage': '85%→92%'
        }
      },
      position: { x: 400, y: 1550 },
    },
    
    // Final Output
    {
      id: 'final-output',
      type: 'collapsibleCode',
      data: { 
        filename: 'Refactored Payment System',
        code: '// Final evolved payment system\n' +
              '// ✅ Cyclomatic complexity: 2 (was 12)\n' +
              '// ✅ Maintainability index: 9/10 (was 2/10)\n' +
              '// ✅ Test coverage: 92% (maintained from 85%)\n' +
              '// ✅ Zero code duplication\n' +
              '// ✅ SOLID principles applied\n' +
              '// ✅ Error handling improved\n' +
              '// ✅ Dependency injection implemented\n' +
              '\n' +
              '📁 src/payment/\n' +
              '├── PaymentService.js (main orchestrator)\n' +
              '├── validators/PaymentValidator.js\n' +
              '├── processors/CardProcessor.js\n' +
              '├── services/NotificationService.js\n' +
              '├── models/PaymentResult.js\n' +
              '└── errors/PaymentErrors.js\n' +
              '\n' +
              '// Metrics improvement:\n' +
              '// • 45min → 5min to understand\n' +
              '// • 4h → 30min to modify\n' +
              '// • Zero breaking changes',
        score: 4.9
      },
      position: { x: 400, y: 1700 },
    },
    
    // Benefits Box
    {
      id: 'benefits',
      data: { label: '✨ Refactoring Benefits\n\n• 83% complexity reduction\n• 350% readability improvement\n• 90% faster onboarding\n• Zero functionality regression\n• Automated refactoring process' },
      position: { x: 800, y: 1650 },
      style: { ...themeStyles.uniqueFeatureNode, width: 250, height: 140 },
    }
  ];
};

const refactoringEdges = [
  // Initial flow
  { id: 'e1', source: 'user-intent', target: 'code-analysis', animated: true, style: themeStyles.edgeStyle },
  { id: 'e2', source: 'code-analysis', target: 'initial-code', style: themeStyles.edgeStyle },
  { id: 'e3', source: 'initial-code', target: 'scoring-label', animated: true, style: themeStyles.edgeStyle },
  
  // Scoring connections
  { id: 'e4', source: 'scoring-label', target: 'complexity-score', style: themeStyles.edgeStyle },
  { id: 'e5', source: 'scoring-label', target: 'maintainability-score', style: themeStyles.edgeStyle },
  { id: 'e6', source: 'scoring-label', target: 'readability-score', style: themeStyles.edgeStyle },
  { id: 'e7', source: 'scoring-label', target: 'test-feedback', style: themeStyles.edgeStyle },
  { id: 'e8', source: 'scoring-label', target: 'linter-feedback', style: themeStyles.edgeStyle },
  
  // Genetic Algorithm
  { id: 'e9', source: 'initial-code', target: 'genetic-algo', style: themeStyles.evolutionEdgeStyle },
  { id: 'e10', source: 'genetic-algo', target: 'ai-agents', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // AI Agents
  { id: 'e11', source: 'ai-agents', target: 'structure-agent', style: themeStyles.edgeStyle },
  { id: 'e12', source: 'ai-agents', target: 'naming-agent', style: themeStyles.edgeStyle },
  { id: 'e13', source: 'ai-agents', target: 'pattern-agent', style: themeStyles.edgeStyle },
  
  // Generation 1
  { id: 'e14', source: 'structure-agent', target: 'gen1-1', style: themeStyles.edgeStyle },
  { id: 'e15', source: 'naming-agent', target: 'gen1-2', style: themeStyles.edgeStyle },
  { id: 'e16', source: 'pattern-agent', target: 'gen1-3', style: themeStyles.edgeStyle },
  { id: 'e17', source: 'ai-agents', target: 'gen1-more', style: { ...themeStyles.edgeStyle, strokeDasharray: '5 5' } },
  
  // Evolution to Gen 1
  { id: 'e18', source: 'gen1-1', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  { id: 'e19', source: 'gen1-2', target: 'evolution-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e20', source: 'gen1-3', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  
  // Feedback loops
  { id: 'f1', source: 'complexity-score', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f2', source: 'test-feedback', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f3', source: 'linter-feedback', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  
  // Generation 2
  { id: 'e21', source: 'evolution-1', target: 'gen2-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e22', source: 'evolution-1', target: 'gen2-2', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Final Evolution
  { id: 'e23', source: 'gen2-1', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  { id: 'e24', source: 'gen2-2', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  
  // Final Output
  { id: 'e25', source: 'evolution-2', target: 'final-output', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e26', source: 'final-output', target: 'benefits', style: themeStyles.edgeStyle },
];

const ArtemisCodeRefactoring = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(refactoringEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      const positions = updatedNodes.map(node => ({
        ...node,
        style: node.style,
        type: node.type,
        data: node.data
      }));
      localStorage.setItem('artemisCodeRefactoringPositions', JSON.stringify(positions));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Reset positions
  const resetPositions = () => {
    localStorage.removeItem('artemisCodeRefactoringPositions');
    setNodes(getInitialNodes());
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #F0F4F8 0%, #D9E2EC 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#102A43', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '26px',
          fontWeight: '700'
        }}>
          🔧 Artemis Code Refactoring - Evolving Code Quality
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(16, 42, 67, 0.08)',
          borderRadius: '20px',
          color: '#486581',
          fontSize: '14px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 100
        }}>
          Genetic algorithms optimize code structure, readability, and maintainability
        </div>
        
        <button
          onClick={resetPositions}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: '#F4511E',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            zIndex: 100
          }}
        >
          Reset Layout
        </button>
        
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          style={{ height: 'calc(100vh - 120px)', marginTop: '120px' }}
        >
          <Background color="#aaa" gap={16} />
          <MiniMap 
            style={{
              background: 'white',
              border: '1px solid #ccc',
              borderRadius: '8px'
            }}
          />
          <Controls 
            style={{
              background: 'white',
              border: '1px solid #ccc',
              borderRadius: '8px'
            }}
          />
        </ReactFlow>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisCodeRefactoring; 